/**
 * Swipe-to-dismiss for the bottom sheet (docs/MOBILE.md §4): a small,
 * framework-agnostic helper. A downward pull
 * translates the panel in real time and releases either into a dismiss (past
 * the threshold) or a spring back.
 *
 * Armed when the pull starts on the grabber, or anywhere while the scrollable
 * body is at its top - so reading long sheet content scrolls, and only the
 * "already at the top, pulling down" gesture dismisses. touchmove is
 * registered non-passive on purpose: an armed pull must preventDefault so the
 * page behind does not rubber-band.
 */
export interface SwipeToDismissOptions {
  /** The element that is translated and dismissed (the sheet panel). */
  panel: HTMLElement;
  /** Always-armed drag handle (the grabber pill row). */
  grabber?: HTMLElement | null;
  /** Scrollable body; a pull elsewhere on the panel arms only when it is scrolled to top. */
  scroller?: HTMLElement | null;
  /** Pull distance (px) past which release dismisses. */
  threshold?: number;
  onDismiss: () => void;
}

/** Attach the gesture; returns a detach function (also resets any transform). */
export function attachSwipeToDismiss({
  panel,
  grabber,
  scroller,
  threshold = 110,
  onDismiss,
}: SwipeToDismissOptions): () => void {
  let startY = 0;
  let delta = 0;
  let armed = false;
  let pulling = false;

  const reset = () => {
    panel.style.transition = 'transform 0.18s ease';
    panel.style.transform = '';
    pulling = false;
    delta = 0;
  };

  const onStart = (e: TouchEvent) => {
    const t = e.touches[0];
    if (!t || e.touches.length > 1) return;
    startY = t.clientY;
    delta = 0;
    const onGrabber = grabber ? grabber.contains(e.target as Node) : false;
    const atTop = (scroller?.scrollTop ?? 0) <= 0;
    armed = onGrabber || atTop;
    // The gesture must own the panel's transform EXCLUSIVELY from here: a
    // still-running CSS animation (the sheet's entrance) outranks an inline
    // transform in the cascade, so a pull that starts inside its window
    // would move nothing - and Safari deferred repainting the panel's
    // background/border for the rest of the gesture (the ghost-drawer bug,
    // 2026-07-11). Finish anything running before the first frame of drag.
    if (armed) panel.getAnimations?.().forEach((a) => a.finish());
    panel.style.transition = '';
  };

  const onMove = (e: TouchEvent) => {
    const t = e.touches[0];
    if (!t || !armed) return;
    delta = t.clientY - startY;
    if (delta <= 0) {
      // Upward: hand the gesture back to scrolling.
      if (!pulling) return;
      delta = 0;
    }
    pulling = true;
    e.preventDefault();
    panel.style.transform = `translateY(${delta}px)`;
  };

  const onEnd = () => {
    if (!pulling) return;
    if (delta > threshold) {
      onDismiss();
      // The panel closes; clear the inline transform for the next open.
      panel.style.transition = '';
      panel.style.transform = '';
      pulling = false;
    } else {
      reset();
    }
  };

  panel.addEventListener('touchstart', onStart, { passive: true });
  panel.addEventListener('touchmove', onMove, { passive: false });
  panel.addEventListener('touchend', onEnd);
  panel.addEventListener('touchcancel', onEnd);
  return () => {
    panel.removeEventListener('touchstart', onStart);
    panel.removeEventListener('touchmove', onMove);
    panel.removeEventListener('touchend', onEnd);
    panel.removeEventListener('touchcancel', onEnd);
    panel.style.transition = '';
    panel.style.transform = '';
  };
}
